import { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabase'
import { saveReferenceCache, loadReferenceCache, clearReferenceCache } from './offlineStore'

// Hardcoded fallbacks (same as original archConfig.js)
export const FALLBACK_EXIST_OPTIONS = [
    { value: 0.5,  label: '×0.5',  desc: 'Básico' },
    { value: 0.75, label: '×0.75', desc: 'Medio' },
    { value: 1.0,  label: '×1.0',  desc: 'Complicado' },
]
export const FALLBACK_ADD_OPTIONS = [
    { value: 1.0, label: '×1.0', desc: 'Normal' },
    { value: 1.2, label: '×1.2', desc: 'Complicada' },
    { value: 1.3, label: '×1.3', desc: 'Flood zone' },
    { value: 1.5, label: '×1.5', desc: 'Compleja' },
    { value: 1.6, label: '×1.6', desc: '2–3 niv. mod.' },
]
export const FALLBACK_REM_OPTIONS = [
    { value: 0.5,  label: '×0.5',  desc: 'Básico' },
    { value: 0.75, label: '×0.75', desc: 'Medio' },
    { value: 1.0,  label: '×1.0',  desc: 'Complicado' },
]

export function useArchAddAltDifficulties() {
    const [rows,    setRows]    = useState([])
    const [loading, setLoading] = useState(true)
    const [error,   setError]   = useState(null)

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        const cached = await loadReferenceCache('arch_add_alt_difficulty_options', null).catch(() => null)
        if (cached) {
            setRows(cached.data)
            setLoading(false)
        }
        const { data, error: err } = await supabase
            .from('arch_add_alt_difficulty_options')
            .select('*')
            .order('sort_order', { ascending: true })
        if (err) {
            console.error('[archAddAltDifficulties] fetch error:', err.message)
            setError(err.message)
            if (!cached) setRows([])
        } else {
            setRows(data || [])
            await saveReferenceCache('arch_add_alt_difficulty_options', data || [], null).catch(() => {})
        }
        setLoading(false)
    }, [])

    useEffect(() => { load() }, [load])

    const getOptionsForComponent = useCallback((component) => {
        const found = rows.filter(r => r.component === component)
            .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
        if (found.length > 0) return found
        if (component === 'exist') return FALLBACK_EXIST_OPTIONS.map((o, i) => ({ ...o, component, sort_order: i, id: `fallback-${i}` }))
        if (component === 'rem')   return FALLBACK_REM_OPTIONS.map((o, i) => ({ ...o, component, sort_order: i, id: `fallback-${i}` }))
        if (component === 'add')   return FALLBACK_ADD_OPTIONS.map((o, i) => ({ ...o, component, sort_order: i, id: `fallback-${i}` }))
        return []
    }, [rows])

    const upsertDifficultyOption = useCallback(async (payload) => {
        const { id, ...rest } = payload
        const dataPayload = {
            component:   payload.component,
            value:       Number(payload.value) || 0,
            label:       payload.label?.trim() || '',
            description: payload.description?.trim() || null,
            sort_order:  payload.sort_order ?? 0,
            updated_at:  new Date().toISOString(),
        }

        if (id) {
            const { data, error: err } = await supabase
                .from('arch_add_alt_difficulty_options')
                .update(dataPayload)
                .eq('id', id)
                .select()
                .single()
            if (err) throw new Error(err.message)
            setRows(prev => prev.map(r => r.id === id ? data : r))
            await clearReferenceCache('arch_add_alt_difficulty_options', null).catch(() => {})
            return data
        }

        const { data, error: err } = await supabase
            .from('arch_add_alt_difficulty_options')
            .insert(dataPayload)
            .select()
            .single()
        if (err) throw new Error(err.message)
        setRows(prev => [...prev, data])
        await clearReferenceCache('arch_add_alt_difficulty_options', null).catch(() => {})
        return data
    }, [])

    const removeDifficultyOption = useCallback(async (id) => {
        setRows(prev => prev.filter(r => r.id !== id))
        const { error: err } = await supabase
            .from('arch_add_alt_difficulty_options')
            .delete()
            .eq('id', id)
        if (err) throw new Error(err.message)
        await clearReferenceCache('arch_add_alt_difficulty_options', null).catch(() => {})
    }, [])

    return {
        rows,
        loading,
        error,
        getOptionsForComponent,
        upsertDifficultyOption,
        removeDifficultyOption,
        reload: load,
    }
}
